/**
 * Phase F1 — Creator Compliance Center
 * ------------------------------------
 * Exposes Phase 2 compliance APIs to the creator UI.
 *
 * Endpoints used (read-only + one mutation):
 *   GET  /api/compliance/me                — current profile incl. status_history
 *   GET  /api/compliance/gating             — full_access boolean + renewal status
 *   POST /api/compliance/creator/submit     — submit / re-submit profile
 *
 * Backend invariants surfaced here:
 *   status      ∈ COMPLIANCE_REQUIRED | COMPLIANCE_PENDING | COMPLIANCE_APPROVED | COMPLIANCE_REJECTED
 *   renewal_status ∈ NOT_DUE | DUE_SOON | OVERDUE
 *
 * No new backend logic. Pure UI.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldAlert, Shield, Clock, FileText, History, AlertCircle, Save,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  COMPLIANCE_REQUIRED: {
    label: 'Compliance Required', color: theme.colors.status.warning, Icon: ShieldAlert,
    description: 'Action needed — complete your compliance profile to unlock platform features.',
  },
  COMPLIANCE_PENDING: {
    label: 'Compliance Pending Review', color: theme.colors.status.info, Icon: Clock,
    description: 'Submitted. An admin is reviewing your profile.',
  },
  COMPLIANCE_APPROVED: {
    label: 'Compliance Approved', color: theme.colors.status.success, Icon: ShieldCheck,
    description: 'You have full platform access. Watch for renewal reminders before expiration.',
  },
  COMPLIANCE_REJECTED: {
    label: 'Compliance Rejected', color: theme.colors.status.error, Icon: ShieldAlert,
    description: 'Your profile was rejected. Review the notes and resubmit.',
  },
};

const inputStyle = {
  width: '100%', padding: '12px 14px', background: theme.colors.bg.page,
  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
  color: theme.colors.text.primary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600,
  color: theme.colors.text.secondary,
};
const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 24,
  boxShadow: theme.colors.shadows.sm,
};
const sectionTitle = {
  fontSize: 16, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 12,
  display: 'flex', alignItems: 'center', gap: 8,
};

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || {
    label: status || 'Unknown', color: theme.colors.text.muted, Icon: Shield,
  };
  const Icon = meta.Icon;
  return (
    <span
      data-testid={`compliance-status-pill-${status}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 14px', borderRadius: 999,
        background: `${meta.color}15`, color: meta.color,
        fontSize: 13, fontWeight: 700, border: `1px solid ${meta.color}40`,
      }}
    >
      <Icon size={14} /> {meta.label}
    </span>
  );
};

const CreatorComplianceCenter = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [gating, setGating] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    legal_name: '', display_name: '', country: '', tax_id_last4: '',
    business_type: 'individual', business_name: '', address_line1: '',
    address_city: '', address_region: '', address_postal: '',
    consent_terms: false, consent_data: false,
  });

  const load = async () => {
    try {
      const [meRes, gateRes] = await Promise.all([
        axios.get(`${API}/compliance/me`),
        axios.get(`${API}/compliance/gating`),
      ]);
      const p = meRes.data.profile || {};
      setProfile(p);
      setGating(gateRes.data);
      // Hydrate form from existing profile when present
      setForm((f) => ({
        ...f,
        legal_name: p.legal_name || '',
        display_name: p.display_name || user?.name || '',
        country: p.country || '',
        tax_id_last4: p.tax_id_last4 || '',
        business_type: p.business_type || 'individual',
        business_name: p.business_name || '',
        address_line1: p.address_line1 || '',
        address_city: p.address_city || '',
        address_region: p.address_region || '',
        address_postal: p.address_postal || '',
        consent_terms: !!p.consent_terms,
        consent_data: !!p.consent_data,
      }));
    } catch (err) {
      toast.error('Failed to load compliance profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleSubmit = async () => {
    if (!form.legal_name || !form.country) {
      toast.error('Legal name and country are required');
      return;
    }
    if (!form.consent_terms || !form.consent_data) {
      toast.error('You must agree to both consent items');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        legal_name: form.legal_name,
        display_name: form.display_name || form.legal_name,
        country: form.country,
        business_type: form.business_type,
        business_name: form.business_type === 'individual' ? null : form.business_name,
        address_line1: form.address_line1 || null,
        address_city: form.address_city || null,
        address_region: form.address_region || null,
        address_postal: form.address_postal || null,
        tax_id_last4: form.tax_id_last4 || null,
        consent_terms: form.consent_terms,
        consent_data: form.consent_data,
      };
      await axios.post(`${API}/compliance/creator/submit`, payload);
      toast.success('Compliance submitted for review');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renewalLabel = useMemo(() => {
    const r = gating?.renewal_status;
    if (r === 'OVERDUE') return { text: 'Renewal OVERDUE', color: theme.colors.status.error };
    if (r === 'DUE_SOON') return { text: 'Renewal Due Soon', color: theme.colors.status.warning };
    return { text: 'Renewal Not Due', color: theme.colors.text.muted };
  }, [gating?.renewal_status]);

  if (loading) {
    return (
      <div style={{ padding: 32 }} data-testid="creator-compliance-loading">
        Loading compliance profile…
      </div>
    );
  }

  const status = profile?.status || 'COMPLIANCE_REQUIRED';
  const meta = STATUS_META[status];
  const canEdit = status === 'COMPLIANCE_REQUIRED' || status === 'COMPLIANCE_REJECTED';

  return (
    <div
      data-testid="creator-compliance-center"
      style={{ padding: 32, maxWidth: 1100, margin: '0 auto' }}
    >
      {/* Heading */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: theme.colors.text.primary,
                     margin: 0 }}>
          Creator Compliance Center
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 6, fontSize: 14 }}>
          Manage your compliance profile, view review history, and watch renewal reminders.
        </p>
      </div>

      {/* Status banner */}
      <div data-testid="compliance-status-banner" style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <StatusPill status={status} />
          <span data-testid="compliance-renewal-pill" style={{
            padding: '4px 10px', borderRadius: 999,
            background: `${renewalLabel.color}15`, color: renewalLabel.color,
            fontSize: 12, fontWeight: 600,
          }}>
            {renewalLabel.text}
          </span>
          {gating?.full_access ? (
            <span data-testid="full-access-flag" style={{
              padding: '4px 10px', borderRadius: 999,
              background: `${theme.colors.status.success}15`,
              color: theme.colors.status.success,
              fontSize: 12, fontWeight: 600,
            }}>FULL ACCESS</span>
          ) : (
            <span data-testid="restricted-access-flag" style={{
              padding: '4px 10px', borderRadius: 999,
              background: `${theme.colors.status.warning}15`,
              color: theme.colors.status.warning,
              fontSize: 12, fontWeight: 600,
            }}>RESTRICTED ACCESS</span>
          )}
        </div>
        <p style={{ marginTop: 12, color: theme.colors.text.secondary, fontSize: 14 }}>
          {meta?.description}
        </p>

        {/* Reminders */}
        {(status === 'COMPLIANCE_REQUIRED' || gating?.renewal_status === 'OVERDUE'
          || gating?.renewal_status === 'DUE_SOON') && (
          <div
            data-testid="compliance-reminder"
            style={{
              marginTop: 14, padding: 12, borderRadius: theme.radius.sm,
              background: `${theme.colors.status.warning}10`,
              border: `1px solid ${theme.colors.status.warning}40`,
              color: theme.colors.text.primary, fontSize: 13,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}
          >
            <AlertCircle size={18} color={theme.colors.status.warning} style={{ flexShrink: 0 }} />
            <div>
              <strong>Reminder:</strong>{' '}
              {status === 'COMPLIANCE_REQUIRED'
                ? 'Complete your profile to start launching portfolios.'
                : gating?.renewal_status === 'OVERDUE'
                  ? `Your compliance expired on ${formatDate(profile?.expiration_date)}. Renew now to regain full access.`
                  : `Your compliance renewal is due by ${formatDate(profile?.renewal_due_date)}.`}
            </div>
          </div>
        )}
      </div>

      {/* Key dates grid */}
      <div data-testid="compliance-dates" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16, marginBottom: 20,
      }}>
        {[
          ['Submitted', profile?.submitted_at],
          ['Approved', profile?.approved_at],
          ['Expiration', profile?.expiration_date],
          ['Renewal Due', profile?.renewal_due_date],
        ].map(([label, value]) => (
          <div key={label} style={cardStyle}>
            <div style={{ fontSize: 12, color: theme.colors.text.muted,
                          textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {label}
            </div>
            <div data-testid={`compliance-date-${label.toLowerCase().replace(/ /g, '-')}`}
                 style={{ marginTop: 6, fontSize: 16, fontWeight: 700,
                          color: theme.colors.text.primary }}>
              {formatDate(value)}
            </div>
          </div>
        ))}
      </div>

      {/* Review notes (if rejected or has notes) */}
      {(profile?.review_notes || status === 'COMPLIANCE_REJECTED') && (
        <div data-testid="review-notes" style={{ ...cardStyle, marginBottom: 20 }}>
          <div style={sectionTitle}><FileText size={18} /> Latest Review Notes</div>
          <div style={{ color: theme.colors.text.primary, whiteSpace: 'pre-wrap',
                        fontSize: 14, lineHeight: 1.55 }}>
            {profile?.review_notes || '—'}
          </div>
        </div>
      )}

      {/* Submit form */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={sectionTitle}><Shield size={18} /> Compliance Profile</div>
        <p style={{ color: theme.colors.text.muted, fontSize: 13, marginTop: -6,
                    marginBottom: 16 }}>
          {canEdit
            ? 'Fill in the form below and submit for admin review.'
            : 'Submitted. Editing is disabled until the next required submission.'}
        </p>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 14,
        }}>
          {[
            ['legal_name', 'Legal Name *'],
            ['display_name', 'Display Name'],
            ['country', 'Country (ISO-2) *'],
            ['business_type', 'Business Type'],
            ['business_name', 'Business Name'],
            ['tax_id_last4', 'Tax ID (last 4)'],
            ['address_line1', 'Address Line 1'],
            ['address_city', 'City'],
            ['address_region', 'Region / State'],
            ['address_postal', 'Postal Code'],
          ].map(([key, label]) => (
            <div key={key}>
              <label style={labelStyle}>{label}</label>
              {key === 'business_type' ? (
                <select
                  data-testid={`compliance-input-${key}`}
                  value={form[key]} disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  style={inputStyle}
                >
                  <option value="individual">individual</option>
                  <option value="llc">llc</option>
                  <option value="corporation">corporation</option>
                  <option value="partnership">partnership</option>
                  <option value="other">other</option>
                </select>
              ) : (
                <input
                  data-testid={`compliance-input-${key}`}
                  type="text" value={form[key]} disabled={!canEdit}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  style={inputStyle}
                />
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              data-testid="compliance-consent-terms"
              type="checkbox" checked={form.consent_terms} disabled={!canEdit}
              onChange={(e) => setForm({ ...form, consent_terms: e.target.checked })}
            />
            I agree to the Kickback Contracts terms of service.
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              data-testid="compliance-consent-data"
              type="checkbox" checked={form.consent_data} disabled={!canEdit}
              onChange={(e) => setForm({ ...form, consent_data: e.target.checked })}
            />
            I consent to data processing for compliance review.
          </label>
        </div>

        <div style={{ marginTop: 18 }}>
          <button
            data-testid="compliance-submit-btn"
            disabled={!canEdit || submitting}
            onClick={handleSubmit}
            style={{
              padding: '10px 22px', borderRadius: theme.radius.sm,
              background: canEdit ? theme.colors.primary : theme.colors.bg.tertiary,
              color: canEdit ? '#fff' : theme.colors.text.muted,
              fontWeight: 700, fontSize: 14, border: 'none',
              cursor: (canEdit && !submitting) ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            <Save size={16} />
            {submitting ? 'Submitting…' : status === 'COMPLIANCE_REJECTED'
              ? 'Resubmit for Review' : 'Submit for Review'}
          </button>
        </div>
      </div>

      {/* Status history */}
      <div style={cardStyle}>
        <div style={sectionTitle}><History size={18} /> Approval History</div>
        {(!profile?.status_history || profile.status_history.length === 0) ? (
          <div style={{ color: theme.colors.text.muted, fontSize: 14 }}>
            No transitions recorded yet.
          </div>
        ) : (
          <div data-testid="compliance-history-list" style={{
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {[...profile.status_history].reverse().map((h, idx) => (
              <div key={idx}
                   data-testid={`compliance-history-row-${idx}`}
                   style={{
                     border: `1px solid ${theme.colors.borders}`,
                     borderRadius: theme.radius.sm, padding: 12,
                     display: 'flex', justifyContent: 'space-between',
                     alignItems: 'center', gap: 12,
                   }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                              flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: theme.colors.text.muted,
                                 padding: '2px 8px', borderRadius: 4,
                                 background: theme.colors.bg.tertiary }}>
                    {h.from || 'INIT'}
                  </span>
                  <span style={{ color: theme.colors.text.secondary }}>→</span>
                  <StatusPill status={h.to} />
                  {h.actor_role && (
                    <span style={{ fontSize: 12, color: theme.colors.text.muted }}>
                      by {h.actor_role}
                    </span>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: theme.colors.text.muted }}>
                    {formatDate(h.at)}
                  </div>
                  {h.notes && (
                    <div style={{ fontSize: 13, color: theme.colors.text.secondary,
                                  marginTop: 4, maxWidth: 320 }}>
                      “{h.notes}”
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorComplianceCenter;
